// components/asset-management/formulaFunctions.ts
// Shared polyfills + preprocessing for the Analyst / asset-model formula engine
// (fast-formula-parser, MIT). Imported real Excel models exposed three gaps that
// made cells render #ERROR / #VALUE! and cascade across the workbook:
//
//  1. fast-formula-parser 1.0.19 ships EMPTY STUBS for MAX, MIN, MEDIAN, CHOOSE and
//     MATCH — calling them throws "Function … is not implemented". We override the
//     stubs with real implementations (config.functions wins over the built-ins).
//  2. It has NO support for LET (Excel-365). Excel serialises LET variables as
//     `_xlpm.NAME`, which the parser can't resolve. We inline LET into a plain
//     expression BEFORE parsing (single- and multi-variable, nested).
//  3. Imported numeric/percent/date literals are stored as their DISPLAY text
//     ("12,500,000", "18.0%", "07/2026") so the grid shows them nicely — but feeding
//     that text to a formula yields #VALUE!. We coerce such strings to numbers when
//     a formula READS a cell (display still uses the original text).
//
// These are additive: existing asset-model formulas that used MAX/MIN/etc. were also
// broken before and now work; formulas without LET / formatted inputs are unchanged.

import { FormulaError } from 'fast-formula-parser';

import { irr } from './finance';

// fast-formula-parser's error singletons. Returning these (not null) lets IFERROR/ISNA wrappers
// behave like Excel when CHOOSE/MATCH/VLOOKUP/etc. fail.
const FErr = FormulaError as { NA: unknown; VALUE: unknown; DIV0: unknown; REF: unknown };

type Arg = unknown;

const cellValue = (a: Arg): unknown =>
  a && typeof a === 'object' && a !== null && 'value' in (a as Record<string, unknown>)
    ? (a as Record<string, unknown>).value
    : a;
const scalar = (v: Arg): unknown => (Array.isArray(v) ? scalar(v[0]) : cellValue(v));
const flatValues = (a: Arg): unknown[] => {
  const out: unknown[] = [];
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) v.forEach(walk);
    else out.push(v);
  };
  walk(cellValue(a));
  return out;
};
const flatNumbers = (a: Arg): number[] =>
  flatValues(a)
    .map((v) => (typeof v === 'number' ? v : Number(v)))
    .filter((n): n is number => Number.isFinite(n));

// Coerce an arg to a finite number (non-numbers -> 0), for array/element-wise math.
const num = (x: Arg): number => {
  const n = Number(cellValue(x));
  return Number.isFinite(n) ? n : 0;
};
// Excel-loose equality (used by array "=" / "<>"): equal by value or string form.
const looseEq = (a: Arg, b: Arg): boolean => {
  const av = cellValue(a);
  const bv = cellValue(b);
  return av === bv || String(av) === String(bv);
};
// Flatten an arg into a 1-D list, remembering whether it was an array (range) or a scalar,
// so _AOP can broadcast scalar<->array element-wise.
const toMatrix = (x: Arg): { list: unknown[]; isArr: boolean } => {
  const v = cellValue(x);
  if (Array.isArray(v)) {
    const list: unknown[] = [];
    const walk = (z: unknown): void => {
      if (Array.isArray(z)) z.forEach(walk);
      else list.push(z);
    };
    walk(v);
    return { list, isArr: true };
  }
  return { list: [v], isArr: false };
};
// One element-wise operator. Comparisons return 1/0 so they compose in arithmetic (mask * value).
const applyArrayOp = (op: string, a: Arg, b?: Arg): number => {
  switch (op) {
    case '*': return num(a) * num(b);
    case '/': return num(a) / num(b);
    case '+': return num(a) + num(b);
    case '-': return num(a) - num(b);
    case '^': return Math.pow(num(a), num(b));
    case '=': return looseEq(a, b) ? 1 : 0;
    case '<>': return looseEq(a, b) ? 0 : 1;
    case '>': return num(a) > num(b) ? 1 : 0;
    case '<': return num(a) < num(b) ? 1 : 0;
    case '>=': return num(a) >= num(b) ? 1 : 0;
    case '<=': return num(a) <= num(b) ? 1 : 0;
    case 'u-': return -num(a);
    case 'u+': return +num(a);
    default: return 0;
  }
};

// fast-formula-parser passes the parser CONTEXT object as the first argument to
// context-needing functions (CHOOSE, MATCH, …). Detect and drop it so our impls only
// ever see the real arguments, regardless of whether a given function is context-bound.
const isContext = (x: unknown): boolean =>
  !!x && typeof x === 'object' && !Array.isArray(x) &&
  'parser' in (x as object) && 'onCell' in (x as object) && 'position' in (x as object);
const realArgs = (args: Arg[]): Arg[] => (args.length && isContext(args[0]) ? args.slice(1) : args);

// A few function NAMES are in fast-formula-parser's `funsNeedContextAndNoDataRetrieve` list
// (SUMIF, INDEX, AVERAGEIF, ROW, IF): for those it passes the parser context as arg0 AND the
// remaining args as RAW references (values NOT retrieved). For our overrides of SUMIF/INDEX/
// AVERAGEIF we must resolve each ref to its value via the context's extractRefValue. When there's
// no context (the normal path, where values are already extracted) this is a no-op passthrough.
const resolveCtxArgs = (args: Arg[]): Arg[] => {
  if (!args.length || !isContext(args[0])) return args;
  const ctx = args[0] as { utils?: { extractRefValue?: (a: unknown) => { val: unknown } } };
  const extract = ctx.utils?.extractRefValue;
  if (typeof extract !== 'function') return args.slice(1);
  return args.slice(1).map((a) => {
    try {
      const r = extract.call(ctx.utils, a);
      return r && typeof r === 'object' && 'val' in r ? r.val : a;
    } catch {
      return a;
    }
  });
};

// 2-D accessor for INDEX / VLOOKUP / XLOOKUP — preserves rows/cols (unlike flatValues which
// flattens). onRange yields a 2-D `arr[r][c]`; a 1-D arg becomes a single column of rows.
const toGrid = (x: Arg): unknown[][] => {
  const v = cellValue(x);
  if (Array.isArray(v)) {
    if (v.length === 0) return [[]];
    if (Array.isArray(v[0])) return v as unknown[][];
    return (v as unknown[]).map((cell) => [cell]);
  }
  return [[v]];
};

// Build a predicate from an Excel criterion (SUMIF/COUNTIF/…): ">100", "<=0", "<>x", "abc", "ab*".
// Numeric comparisons require numeric values; equality is case-insensitive with * / ? wildcards.
const makeMatcher = (crit: Arg): ((v: unknown) => boolean) => {
  const c = scalar(crit);
  const s = c === null || c === undefined ? '' : String(c);
  const m = /^(<=|>=|<>|<|>|=)([\s\S]*)$/.exec(s);
  const op = m ? m[1]! : '=';
  const rhs = m ? m[2]! : s;
  const rhsNum = Number(rhs);
  const rhsIsNum = rhs.trim() !== '' && Number.isFinite(rhsNum);
  if (op === '>' || op === '<' || op === '>=' || op === '<=') {
    return (v) => {
      const nv = Number(cellValue(v));
      if (!Number.isFinite(nv) || !rhsIsNum) return false;
      return op === '>' ? nv > rhsNum : op === '<' ? nv < rhsNum : op === '>=' ? nv >= rhsNum : nv <= rhsNum;
    };
  }
  const eq = (v: unknown): boolean => {
    const vv = cellValue(v);
    if (rhsIsNum) {
      const nv = Number(vv);
      return Number.isFinite(nv) && nv === rhsNum;
    }
    const sv = String(vv ?? '');
    if (rhs.includes('*') || rhs.includes('?')) {
      const re = new RegExp(`^${rhs.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.')}$`, 'i');
      return re.test(sv);
    }
    return sv.toLowerCase() === rhs.toLowerCase();
  };
  return op === '<>' ? (v) => !eq(v) : eq;
};

/** Pull a flat list of finite numbers out of a formula argument (scalar, cell, or range). */
function extractNumbers(arg: unknown): number[] {
  const raw =
    arg && typeof arg === 'object' && arg !== null && 'value' in (arg as Record<string, unknown>)
      ? (arg as Record<string, unknown>).value
      : arg;
  const out: number[] = [];
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) {
      v.forEach(walk);
      return;
    }
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return;
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out.push(n);
  };
  walk(raw);
  return out;
}

/** NPV at a discount rate of a series of period cash flows (period 1..n, Excel convention). */
function npv(rate: number, vals: number[]): number {
  if (!Number.isFinite(rate)) return NaN;
  return vals.reduce((s, v, i) => s + v / Math.pow(1 + rate, i + 1), 0);
}

/**
 * Real implementations for the functions fast-formula-parser stubs out, plus the financial
 * functions it omits entirely (IRR / NPV / EQMULT). Shared by both engines — the in-grid parser
 * (formulaParser.ts) and the workbook engine (workbookEngine.ts) — so `=IRR(...)` means exactly
 * the same thing in a custom tab and in an imported multi-sheet model.
 */
export const extraFormulaFunctions = {
  IRR: (flows: unknown, guess?: unknown) =>
    irr(extractNumbers(flows), guess !== undefined ? extractNumbers(guess)[0] ?? 0.1 : 0.1),
  NPV: (rate: unknown, ...vals: unknown[]) => npv(extractNumbers(rate)[0] ?? NaN, vals.flatMap(extractNumbers)),
  // Equity multiple of a cash-flow series: total distributions / total contributions.
  // (fast-formula-parser lacks SUMIF, so this is the robust way to express it.)
  EQMULT: (flows: unknown) => {
    const nums = extractNumbers(flows);
    const pos = nums.reduce((s, n) => (n > 0 ? s + n : s), 0);
    const neg = nums.reduce((s, n) => (n < 0 ? s + n : s), 0);
    return neg < 0 ? Math.round((pos / -neg) * 1000) / 1000 : 0;
  },
  MAX: (...a: Arg[]) => {
    const n = realArgs(a).flatMap(flatNumbers);
    return n.length ? Math.max(...n) : 0;
  },
  MIN: (...a: Arg[]) => {
    const n = realArgs(a).flatMap(flatNumbers);
    return n.length ? Math.min(...n) : 0;
  },
  MEDIAN: (...a: Arg[]) => {
    const n = realArgs(a).flatMap(flatNumbers).sort((x, y) => x - y);
    if (!n.length) return 0;
    const m = n.length >> 1;
    return n.length % 2 ? n[m]! : (n[m - 1]! + n[m]!) / 2;
  },
  CHOOSE: (...a: Arg[]) => {
    const [idx, ...choices] = realArgs(a);
    const i = Math.trunc(Number(scalar(idx)));
    if (!Number.isFinite(i) || i < 1 || i > choices.length) return FErr.VALUE;
    return scalar(choices[i - 1]!);
  },
  MATCH: (...a: Arg[]) => {
    const [lk, rg, ty] = realArgs(a);
    const lookup = scalar(lk);
    const arr = flatValues(rg);
    const t = ty !== undefined ? Math.trunc(Number(scalar(ty))) : 1;
    if (t === 0) {
      const i = arr.findIndex((v) => v === lookup || String(v) === String(lookup));
      return i >= 0 ? i + 1 : FErr.NA;
    }
    const L = Number(lookup);
    let pos = -1;
    if (t === 1) {
      for (let i = 0; i < arr.length; i += 1) {
        const n = Number(arr[i] ?? NaN);
        if (Number.isFinite(n) && n <= L) pos = i;
        else if (Number.isFinite(n) && n > L) break;
      }
    } else {
      for (let i = 0; i < arr.length; i += 1) {
        const n = Number(arr[i] ?? NaN);
        if (Number.isFinite(n) && n >= L) pos = i;
        else if (Number.isFinite(n) && n < L) break;
      }
    }
    return pos >= 0 ? pos + 1 : FErr.NA;
  },
  // Element-wise array operator. Emitted by rewriteArray() so fast-formula-parser (which has NO
  // array/CSE support) can evaluate Excel array expressions like (D17:D153="Occupé")*F17:F153.
  // First arg is the operator name; remaining are operands (scalar or range), broadcast per element.
  _AOP: (...a: Arg[]) => {
    const ar = realArgs(a);
    const op = String(scalar(ar[0]));
    if (op === 'u-' || op === 'u+') {
      const m = toMatrix(ar[1]);
      const r = m.list.map((x) => applyArrayOp(op, x));
      return m.isArr ? r.map((v) => [v]) : r[0];
    }
    const L = toMatrix(ar[1]);
    const R = toMatrix(ar[2]);
    if (!L.isArr && !R.isArr) return applyArrayOp(op, L.list[0], R.list[0]);
    const n = Math.max(L.isArr ? L.list.length : 1, R.isArr ? R.list.length : 1);
    const out: number[][] = [];
    for (let i = 0; i < n; i += 1) {
      out.push([applyArrayOp(op, L.isArr ? L.list[i] : L.list[0], R.isArr ? R.list[i] : R.list[0])]);
    }
    return out;
  },
  // Row-number array for ROW(range). The stored Excel formula already carries literal per-row
  // bounds (Excel expands relative refs per cell), so we just parse the row numbers from the ref.
  _AROW: (...a: Arg[]) => {
    const s = String(scalar(realArgs(a)[0] ?? ''));
    const m = s.match(/\$?[A-Za-z]+\$?(\d+):\$?[A-Za-z]+\$?(\d+)/);
    if (!m) return 0;
    const r1 = Number(m[1]);
    const r2 = Number(m[2]);
    const out: number[][] = [];
    for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r += 1) out.push([r]);
    return out;
  },
  // Override SUMPRODUCT to robustly consume arrays (incl. _AOP output) and scalars. fast-formula-
  // parser's built-in chokes on a function-returned array; this sums the element-wise product.
  SUMPRODUCT: (...a: Arg[]) => {
    const ar = realArgs(a).map(toMatrix);
    if (ar.length === 1) return ar[0]!.list.reduce((s: number, x) => s + num(x), 0);
    const n = Math.max(...ar.map((m) => (m.isArr ? m.list.length : 1)));
    let s = 0;
    for (let i = 0; i < n; i += 1) {
      let p = 1;
      for (const m of ar) p *= num(m.isArr ? m.list[i] : m.list[0]);
      s += p;
    }
    return s;
  },

  // --- conditional aggregates + lookups (fast-formula-parser ships none of these) ---
  SUMIF: (...a: Arg[]) => {
    const ar = resolveCtxArgs(a);
    const range = flatValues(ar[0]);
    const match = makeMatcher(ar[1]);
    const sumRange = ar[2] !== undefined ? flatValues(ar[2]) : range;
    let s = 0;
    for (let i = 0; i < range.length; i += 1) if (match(range[i])) s += num(sumRange[i]);
    return s;
  },
  SUMIFS: (...a: Arg[]) => {
    const ar = realArgs(a);
    const sumRange = flatValues(ar[0]);
    const pairs: Array<{ r: unknown[]; m: (v: unknown) => boolean }> = [];
    for (let i = 1; i + 1 < ar.length; i += 2) pairs.push({ r: flatValues(ar[i]), m: makeMatcher(ar[i + 1]) });
    let s = 0;
    for (let i = 0; i < sumRange.length; i += 1) if (pairs.every((p) => p.m(p.r[i]))) s += num(sumRange[i]);
    return s;
  },
  COUNTIF: (...a: Arg[]) => {
    const ar = realArgs(a);
    const match = makeMatcher(ar[1]);
    return flatValues(ar[0]).reduce((c: number, v) => (match(v) ? c + 1 : c), 0);
  },
  COUNTIFS: (...a: Arg[]) => {
    const ar = realArgs(a);
    const pairs: Array<{ r: unknown[]; m: (v: unknown) => boolean }> = [];
    for (let i = 0; i + 1 < ar.length; i += 2) pairs.push({ r: flatValues(ar[i]), m: makeMatcher(ar[i + 1]) });
    const n = pairs.length ? pairs[0]!.r.length : 0;
    let c = 0;
    for (let i = 0; i < n; i += 1) if (pairs.every((p) => p.m(p.r[i]))) c += 1;
    return c;
  },
  AVERAGEIF: (...a: Arg[]) => {
    const ar = resolveCtxArgs(a);
    const range = flatValues(ar[0]);
    const match = makeMatcher(ar[1]);
    const avg = ar[2] !== undefined ? flatValues(ar[2]) : range;
    let s = 0;
    let c = 0;
    for (let i = 0; i < range.length; i += 1)
      if (match(range[i])) { const nv = Number(cellValue(avg[i])); if (Number.isFinite(nv)) { s += nv; c += 1; } }
    return c ? s / c : FErr.DIV0;
  },
  AVERAGEIFS: (...a: Arg[]) => {
    const ar = realArgs(a);
    const avgRange = flatValues(ar[0]);
    const pairs: Array<{ r: unknown[]; m: (v: unknown) => boolean }> = [];
    for (let i = 1; i + 1 < ar.length; i += 2) pairs.push({ r: flatValues(ar[i]), m: makeMatcher(ar[i + 1]) });
    let s = 0;
    let c = 0;
    for (let i = 0; i < avgRange.length; i += 1)
      if (pairs.every((p) => p.m(p.r[i]))) { const nv = Number(cellValue(avgRange[i])); if (Number.isFinite(nv)) { s += nv; c += 1; } }
    return c ? s / c : FErr.DIV0;
  },
  VLOOKUP: (...a: Arg[]) => {
    const ar = realArgs(a);
    const lookup = scalar(ar[0]);
    const table = toGrid(ar[1]);
    const colIdx = Math.trunc(Number(scalar(ar[2])));
    if (!Number.isFinite(colIdx) || colIdx < 1) return FErr.VALUE;
    const approx =
      ar[3] === undefined
        ? true
        : (() => { const x = scalar(ar[3]); return x === true || (typeof x === 'number' ? x !== 0 : String(x).toUpperCase() === 'TRUE'); })();
    let matchRow = -1;
    if (!approx) {
      for (let r = 0; r < table.length; r += 1) if (looseEq(table[r]![0], lookup)) { matchRow = r; break; }
    } else {
      const L = Number(lookup);
      for (let r = 0; r < table.length; r += 1) {
        const v = Number(cellValue(table[r]![0]));
        if (Number.isFinite(v) && v <= L) matchRow = r;
        else if (Number.isFinite(v) && v > L) break;
      }
    }
    if (matchRow < 0) return FErr.NA;
    const row = table[matchRow]!;
    return colIdx > row.length ? FErr.REF : cellValue(row[colIdx - 1]);
  },
  XLOOKUP: (...a: Arg[]) => {
    const ar = realArgs(a);
    const lookup = scalar(ar[0]);
    const lookupArr = flatValues(ar[1]);
    const returnArr = flatValues(ar[2]);
    const idx = lookupArr.findIndex((v) => looseEq(v, lookup));
    if (idx < 0) return ar[3] !== undefined ? scalar(ar[3]) : FErr.NA;
    return cellValue(returnArr[idx]);
  },
  INDEX: (...a: Arg[]) => {
    const ar = resolveCtxArgs(a);
    const grid = toGrid(ar[0]);
    const rowNum = Math.trunc(Number(scalar(ar[1])));
    const colNum = ar[2] !== undefined ? Math.trunc(Number(scalar(ar[2]))) : undefined;
    if (colNum === undefined) {
      if (grid.length === 1) { const cell = grid[0]![rowNum - 1]; return cell === undefined ? FErr.REF : cellValue(cell); }
      if ((grid[0]?.length ?? 0) === 1) { const r = grid[rowNum - 1]; return r ? cellValue(r[0]) : FErr.REF; }
      const r = grid[rowNum - 1];
      return r ? r.map(cellValue) : FErr.REF;
    }
    const r = grid[rowNum - 1];
    if (!r) return FErr.REF;
    const cell = r[colNum - 1];
    return cell === undefined ? FErr.REF : cellValue(cell);
  },
};

// ---------------------------------------------------------------------------
// LET desugaring — inline `LET(_xlpm.a, valA, [_xlpm.b, valB, …], body)` by
// substituting each variable with its (parenthesised) value. Excel always names
// LET variables `_xlpm.NAME`, so token substitution is safe.
// ---------------------------------------------------------------------------
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function splitTopLevelArgs(s: string): string[] {
  const args: string[] = [];
  let depth = 0;
  let inStr = false;
  let strCh = '';
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) {
      cur += ch;
      if (ch === strCh) inStr = false;
      continue;
    }
    if (ch === '"' || ch === "'") { inStr = true; strCh = ch; cur += ch; continue; }
    if (ch === '(' || ch === '[' || ch === '{') { depth += 1; cur += ch; continue; }
    if (ch === ')' || ch === ']' || ch === '}') { depth -= 1; cur += ch; continue; }
    if (ch === ',' && depth === 0) { args.push(cur); cur = ''; continue; }
    cur += ch;
  }
  args.push(cur);
  return args;
}

function matchingParen(s: string, open: number): number {
  let depth = 0;
  let inStr = false;
  let strCh = '';
  for (let i = open; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) { if (ch === strCh) inStr = false; continue; }
    if (ch === '"' || ch === "'") { inStr = true; strCh = ch; continue; }
    if (ch === '(') depth += 1;
    else if (ch === ')') { depth -= 1; if (depth === 0) return i; }
  }
  return -1;
}

// Find the last `LET(` that is NOT part of a longer identifier and NOT inside a string literal.
// (A formula can legitimately contain the text "LET(" inside a quoted string — e.g. a label or a
// CONCAT argument — and rewriting that would corrupt the formula, so string contents are skipped.)
function findLastLet(s: string): number {
  let last = -1;
  let inStr = false;
  let strCh = '';
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inStr) { if (ch === strCh) inStr = false; continue; }
    if (ch === '"' || ch === "'") { inStr = true; strCh = ch; continue; }
    if ((ch === 'L' || ch === 'l') && /^let\s*\(/i.test(s.slice(i, i + 10))) {
      const prev = i > 0 ? s[i - 1]! : '';
      if (!/[A-Za-z0-9_.]/.test(prev)) last = i;
    }
  }
  return last;
}

function substituteVar(str: string, name: string, replacement: string): string {
  const re = new RegExp(`(^|[^A-Za-z0-9_.])${escapeRe(name)}(?![A-Za-z0-9_.])`, 'g');
  return str.replace(re, (_full, before: string) => before + replacement);
}

/** Inline every LET(...) in a formula into an equivalent plain expression. */
export function desugarLet(formula: string): string {
  // Cheap early-out: desugarLet runs on every formula parse (every cell, every recompute), but
  // almost none contain LET. Skip all regex/scan work unless the substring is present.
  if (formula.indexOf('LET') === -1 && formula.indexOf('let') === -1) return formula;
  let out = formula;
  let guard = 0;
  // Innermost-first (last `LET(` in the string), one per pass, until none remain.
  while (guard < 200) {
    guard += 1;
    const letPos = findLastLet(out);
    if (letPos === -1) break;
    let p = letPos;
    while (p < out.length && out[p] !== '(') p += 1;
    const close = matchingParen(out, p);
    if (close === -1) break;
    const args = splitTopLevelArgs(out.slice(p + 1, close)).map((a) => a.trim());
    // LET needs an odd count: (name,value) pairs + a final body. Bail on anything odd.
    if (args.length < 3 || args.length % 2 === 0) break;
    const pairs: Array<[string, string]> = [];
    for (let i = 0; i + 1 < args.length - 1; i += 2) pairs.push([args[i]!, args[i + 1]!]);
    let body = args[args.length - 1]!;
    const resolved: string[] = [];
    for (let i = 0; i < pairs.length; i += 1) {
      let val = pairs[i]![1];
      for (let j = 0; j < i; j += 1) val = substituteVar(val, pairs[j]![0], `(${resolved[j]})`);
      resolved.push(val);
    }
    for (let i = 0; i < pairs.length; i += 1) body = substituteVar(body, pairs[i]![0], `(${resolved[i]})`);
    out = out.slice(0, letPos) + `(${body})` + out.slice(close + 1);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Formatted-input coercion — imported literals are stored as display text. When a
// formula reads such a cell we turn "12,500,000" -> 12500000, "18.0%" -> 0.18 and
// "07/2026" -> Excel serial, while genuine text (e.g. "Occupé") is left untouched.
// ---------------------------------------------------------------------------
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

// Excel serial for a Y/M/D, or null if the date is out of range / would roll over (e.g. 13/2026,
// 02/31). Rejecting invalid dates avoids turning a numeric-looking TEXT label into a bogus serial.
function toExcelSerial(y: number, mo: number, d: number): number | null {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const t = Date.UTC(y, mo - 1, d);
  const dt = new Date(t);
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return Math.round((t - EXCEL_EPOCH) / 86400000);
}

export function coerceFormulaInput(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const s = v.trim();
  // Cheap guard: a value with no digit can't be a formatted number/date — skip the regex battery.
  if (s === '' || !/\d/.test(s)) return v;
  // Percent (strong signal: trailing %). Allow thousands grouping in the magnitude.
  let m = s.match(/^[+-]?\d[\d,]*(?:\.\d+)?\s*%$/);
  if (m) return Number(s.replace(/[%,\s]/g, '')) / 100;
  // Currency: REQUIRE a currency symbol (strong signal). The symbol may be a PREFIX ("€2,400,000")
  // or a SUFFIX ("2 400 000 €", European). Optional parens = negative. Thousands separator may be a
  // comma, ordinary space, or non-breaking space. Without a symbol a bare "1,234" could be a genuine
  // code/label, so the `/[€$£]/` guard keeps us from coercing those.
  m = s.match(/^(\()?\s*[€$£]?\s*(\d[\d,\s ]*(?:\.\d+)?)\s*[€$£]?\s*\)?$/);
  // Require BALANCED parens: "(€1,234)" is accounting-negative; "($100" (unbalanced) stays text.
  if (m && /[€$£]/.test(s) && s.includes('(') === s.includes(')')) {
    let n = Number((m[2] ?? '').replace(/[,\s ]/g, ''));
    if (m[1]) n = -n;
    return Number.isFinite(n) ? n : v;
  }
  // Bare thousands-grouped number: STRICT groups of three with at least one separator, so
  // "12,500,000" coerces but "12,34" / "1,2,3,4" (which aren't real groupings) do not.
  m = s.match(/^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/);
  if (m) return Number(s.replace(/,/g, ''));
  // Dates -> Excel serial, with range/rollover validation.
  let serial: number | null = null;
  if ((m = s.match(/^(\d{1,2})\/(\d{4})$/))) serial = toExcelSerial(Number(m[2]), Number(m[1]), 1);
  else if ((m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) serial = toExcelSerial(Number(m[3]), Number(m[1]), Number(m[2]));
  else if ((m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) serial = toExcelSerial(Number(m[1]), Number(m[2]), Number(m[3]));
  return serial !== null ? serial : v;
}

// ---------------------------------------------------------------------------
// Array-formula rewriting — fast-formula-parser has NO array/CSE support, so Excel array
// expressions like `(D17:D153="Occupé")*F17:F153*G17:G153` or `ROW($M$18:M18)` evaluate to
// #VALUE!. We tokenise + parse the formula, and where a binary/unary operator (or ROW) has a
// RANGE operand we rewrite it into a call to our element-wise `_AOP(...)` / `_AROW(...)` helpers,
// which broadcast and return arrays. Formulas with no array operator are returned BYTE-IDENTICAL
// (we only return the rewrite when it actually introduced _AOP/_AROW), so plain `SUM(A1:A9)` etc.
// are never touched. Run alongside desugarLet() before parsing.
// ---------------------------------------------------------------------------
interface FToken { t: string; v: string }
interface FNode {
  k: string;
  v?: string;
  op?: string;
  arg?: FNode;
  left?: FNode;
  right?: FNode;
  name?: string;
  args?: FNode[];
}

function tokenizeFormula(s: string): FToken[] {
  const toks: FToken[] = [];
  let i = 0;
  const isD = (c: string): boolean => c >= '0' && c <= '9';
  while (i < s.length) {
    const c = s[i]!;
    if (c === ' ' || c === '\t' || c === '\n') { i += 1; continue; }
    if (c === '"') {
      let j = i + 1;
      let v = '"';
      while (j < s.length) {
        v += s[j];
        if (s[j] === '"') { if (s[j + 1] === '"') { v += '"'; j += 2; continue; } j += 1; break; }
        j += 1;
      }
      toks.push({ t: 'str', v }); i = j; continue;
    }
    if (c === "'") { // 'Sheet name'!Ref
      let j = i + 1;
      let name = "'";
      while (j < s.length) { name += s[j]; if (s[j] === "'") { j += 1; break; } j += 1; }
      let ref = name;
      if (s[j] === '!') { ref += '!'; j += 1; while (j < s.length && /[A-Za-z0-9_$:.]/.test(s[j]!)) { ref += s[j]; j += 1; } }
      toks.push({ t: 'ref', v: ref }); i = j; continue;
    }
    if (isD(c) || (c === '.' && isD(s[i + 1] ?? ''))) {
      let j = i;
      let v = '';
      while (j < s.length && /[0-9.]/.test(s[j]!)) { v += s[j]; j += 1; }
      if (s[j] === 'e' || s[j] === 'E') { v += s[j]; j += 1; if (s[j] === '+' || s[j] === '-') { v += s[j]; j += 1; } while (j < s.length && isD(s[j]!)) { v += s[j]; j += 1; } }
      toks.push({ t: 'num', v }); i = j; continue;
    }
    if (/[A-Za-z_$]/.test(c)) {
      let j = i;
      let v = '';
      while (j < s.length && /[A-Za-z0-9_$.]/.test(s[j]!)) { v += s[j]; j += 1; }
      if (s[j] === '(') { toks.push({ t: 'func', v }); i = j; continue; }
      if (s[j] === ':') { v += ':'; j += 1; while (j < s.length && /[A-Za-z0-9_$]/.test(s[j]!)) { v += s[j]; j += 1; } toks.push({ t: 'ref', v }); i = j; continue; }
      toks.push({ t: 'ref', v }); i = j; continue;
    }
    const two = s.slice(i, i + 2);
    if (two === '<=' || two === '>=' || two === '<>') { toks.push({ t: 'op', v: two }); i += 2; continue; }
    if ('+-*/^&=<>(),%'.includes(c)) { toks.push({ t: c === '(' || c === ')' || c === ',' ? c : 'op', v: c }); i += 1; continue; }
    toks.push({ t: 'raw', v: c }); i += 1;
  }
  return toks;
}

function parseTokens(toks: FToken[]): FNode {
  let p = 0;
  const peek = (): FToken | undefined => toks[p];
  const next = (): FToken | undefined => toks[p++];
  const PREC: Record<string, number> = { '=': 1, '<>': 1, '<': 1, '>': 1, '<=': 1, '>=': 1, '&': 2, '+': 3, '-': 3, '*': 4, '/': 4, '^': 5 };
  function parseExpr(min: number): FNode {
    let left = parseUnary();
    let t = peek();
    while (t && t.t === 'op' && PREC[t.v] !== undefined && PREC[t.v]! >= min) {
      const op = next()!.v;
      const right = parseExpr(PREC[op]! + 1);
      left = { k: 'bin', op, left, right };
      t = peek();
    }
    return left;
  }
  function parseUnary(): FNode {
    const t = peek();
    if (t && t.t === 'op' && (t.v === '-' || t.v === '+')) { const op = next()!.v; return { k: 'un', op, arg: parseUnary() }; }
    return parsePostfix();
  }
  function parsePostfix(): FNode {
    let n = parsePrimary();
    let t = peek();
    while (t && t.t === 'op' && t.v === '%') { next(); n = { k: 'pct', arg: n }; t = peek(); }
    return n;
  }
  function parsePrimary(): FNode {
    const t = peek();
    if (!t) return { k: 'empty' };
    if (t.t === '(') { next(); const e = parseExpr(1); if (peek()?.t === ')') next(); return { k: 'paren', arg: e }; }
    if (t.t === 'num') { next(); return { k: 'num', v: t.v }; }
    if (t.t === 'str') { next(); return { k: 'str', v: t.v }; }
    if (t.t === 'ref') { next(); return { k: 'ref', v: t.v }; }
    if (t.t === 'func') {
      const name = next()!.v;
      next(); // consume '('
      const args: FNode[] = [];
      if (peek()?.t !== ')') {
        args.push(parseExpr(1));
        while (peek()?.t === ',') { next(); args.push(parseExpr(1)); }
      }
      if (peek()?.t === ')') next();
      return { k: 'func', name, args };
    }
    next();
    return { k: 'raw', v: t.v };
  }
  return parseExpr(1);
}

const isRangeRef = (v: string): boolean => v.indexOf(':') !== -1;
function isArrNode(n: FNode | undefined): boolean {
  if (!n) return false;
  switch (n.k) {
    case 'ref': return isRangeRef(n.v ?? '');
    case 'bin': return isArrNode(n.left) || isArrNode(n.right);
    case 'un':
    case 'pct':
    case 'paren': return isArrNode(n.arg);
    case 'func': {
      const a = n.args;
      return (n.name ?? '').toUpperCase() === 'ROW' && !!a && a.length === 1 && a[0]!.k === 'ref' && isRangeRef(a[0]!.v ?? '');
    }
    default: return false;
  }
}
function emitNode(n: FNode): string {
  switch (n.k) {
    case 'num':
    case 'str':
    case 'ref':
    case 'raw': return n.v ?? '';
    case 'empty': return '';
    case 'pct': return `(${emitNode(n.arg!)}/100)`;
    case 'paren': return `(${emitNode(n.arg!)})`;
    case 'un': return isArrNode(n.arg) ? `_AOP("u${n.op}",${emitNode(n.arg!)})` : `${n.op}(${emitNode(n.arg!)})`;
    case 'func':
      if ((n.name ?? '').toUpperCase() === 'ROW' && isArrNode(n)) return `_AROW("${n.args![0]!.v}")`;
      return `${n.name}(${(n.args ?? []).map(emitNode).join(',')})`;
    case 'bin':
      // `&` (string concat) has no element-wise form in _AOP (whose default is numeric 0) — emit it
      // scalar so an array concat surfaces as #VALUE! rather than silently evaluating to 0.
      if (n.op !== '&' && (isArrNode(n.left) || isArrNode(n.right)))
        return `_AOP("${n.op}",${emitNode(n.left!)},${emitNode(n.right!)})`;
      return `${emitNode(n.left!)}${n.op}${emitNode(n.right!)}`;
    default: return '';
  }
}

/**
 * Rewrite Excel array-operator expressions into `_AOP(...)` / `_AROW(...)` calls so the (array-less)
 * parser can evaluate them. Returns the formula UNCHANGED unless an actual array operator was found.
 */
export function rewriteArray(formula: string): string {
  if (formula.indexOf(':') === -1) return formula; // no range ref -> nothing array-valued
  try {
    const out = emitNode(parseTokens(tokenizeFormula(formula)));
    return out.includes('_AOP') || out.includes('_AROW') ? out : formula;
  } catch {
    return formula;
  }
}
